import { useState, useCallback, useRef, useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { useFormContext } from '@/context/FormContext';
import { Questionnaire } from '@/typings/Questionnaire';
import Button from '@/components/DesignLibrary/Button';
import { useToast } from '@/hooks';

import getListOrTableField from '@helpers/getListOrTableField';
import trackEvents from '@/helpers/trackEvents';
import formValuesToResponses from '../helpers/formValuesToResponses';

type Props = {
  formData: Questionnaire.Questionnaire;
  shouldForceComplete?: boolean;
};

const SubmitButton: React.FC<Props> = ({ formData, shouldForceComplete }) => {
  trackEvents.track('render - SaveButton');
  const toast = useToast();
  const {
    fieldsById,
    getFieldSource,
    getAiRequestId,
    onSubmit,
  } = useFormContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const handleSubmit = useCallback(async (data: FieldValues): Promise<void> => {
    setIsSubmitting(true);
    const asResponses = formValuesToResponses(
      data,
      getFieldSource,
      getAiRequestId,
      formData.sections,
      (fieldId: string) => Boolean(getListOrTableField({ fieldId, fieldsById })),
    );
    const success = await onSubmitRef.current(asResponses, true);
    if (success) {
      toast(useToast.ToastTypeVariants.SUCCESS, 'Responses submitted successfully!');
    }
    setIsSubmitting(false);
  }, [formData.sections, toast, fieldsById, getFieldSource, getAiRequestId]);

  useEffect(() => {
    if (shouldForceComplete) {
      handleSubmit(formData);
    }
  }, [shouldForceComplete, formData, handleSubmit]);

  return (
    <Button
      type="submit"
      variant="primary"
      size="md"
      onClick={() => handleSubmit(formData)}
      loading={isSubmitting}
    >
      Submit
    </Button>
  );
};

export default SubmitButton;